'use client';

import Image from 'next/image';

import { formatPrice, quotedTotal } from '@/lib/price';
import { cn } from '@holistic/components';
import {
  ImageOff,
  Info,
  Landmark,
  Repeat2,
  ShieldCheck,
  Truck,
  X,
} from '@holistic/components/icons';

import type { StepParam } from './constants';
import type { PreviewRow, TalepDevice, TalepRequestType } from './types';

const guarantees = [
  {
    icon: Truck,
    title: 'Ücretsiz çift yönlü kargo',
    description: 'Cihazınızı kapınızdan alır, ücretsiz getiririz.',
  },
  {
    icon: Landmark,
    title: 'Bakanlık onaylı yenileme merkezi',
    description: 'Resmî olarak yetkilendirilmiş tesis.',
  },
  {
    icon: ShieldCheck,
    title: 'Garantili onarım',
    description: 'Parça ve işçilik garantisi.',
  },
];

type SelectionCardProps = {
  device: TalepDevice | null;
  /** Seçilen arızalar; fiyat dökümü ve toplam buradan çıkar. */
  requestTypes: TalepRequestType[];
  isWarrantyCovered?: boolean;
  /** Ödeme ekranında doldurulan kargo bilgileri; salt önizleme. */
  preview?: PreviewRow[];
  /** Bir adıma geri dönmek için (cihaz kartındaki "Değiştir"). */
  onEdit: (key: StepParam) => void;
  /**
   * "Değiştir"in götüreceği adım. Cihazı değiştirmek en baştan başlamak
   * demek olmamalı: model seçilmişse model, değilse seri/marka adımına döner.
   */
  deviceEditKey?: StepParam;
  /** Arıza satırındaki × ile tek arıza kaldırma. */
  onRemoveType?: (id: string) => void;
  /** Başlıktaki "Temizle" — tüm arıza seçimini sıfırlar. */
  onClearTypes?: () => void;
  className?: string;
};

const SelectionCard = ({
  device,
  requestTypes,
  isWarrantyCovered = false,
  preview,
  onEdit,
  deviceEditKey = 'model',
  onRemoveType,
  onClearTypes,
  className,
}: SelectionCardProps) => {
  const total = quotedTotal(requestTypes);

  return (
    <div
      className={cn(
        'space-y-5 rounded-2xl border border-gray-100 bg-white p-5 shadow-xs',
        className
      )}
    >
      <div className="flex items-center justify-between gap-3">
        <p className="text-base font-semibold text-gray-900">
          Seçimleriniz{requestTypes.length > 0 && ` (${requestTypes.length})`}
        </p>
        {requestTypes.length > 0 && onClearTypes && (
          <button
            type="button"
            onClick={onClearTypes}
            className="text-primary cursor-pointer text-sm font-medium hover:underline"
          >
            Temizle
          </button>
        )}
      </div>

      {device && (
        <div className="flex items-center gap-3 rounded-xl border border-gray-100 p-3">
          <span className="flex size-14 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-gray-50">
            {device.imageUrl ? (
              <Image
                src={device.imageUrl}
                alt={device.title}
                width={56}
                height={56}
                className="size-full object-contain p-1.5"
              />
            ) : (
              <ImageOff className="size-4 text-gray-300" />
            )}
          </span>

          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold text-gray-900">
              {device.title}
            </p>
            <p className="truncate text-xs text-gray-500">{device.subtitle}</p>
            <button
              type="button"
              onClick={() => onEdit(deviceEditKey)}
              className="text-primary mt-1 flex cursor-pointer items-center gap-1 text-xs font-medium hover:underline"
            >
              <Repeat2 className="size-3.5" />
              Değiştir
            </button>
          </div>
        </div>
      )}

      {requestTypes.length > 0 && (
        <div className="space-y-3 border-t border-gray-100 pt-4">
          <p className="text-sm font-semibold text-gray-900">
            Seçilen arızalar
          </p>

          <ul className="space-y-2">
            {requestTypes.map((item) => (
              <li
                key={item.id}
                className="flex items-center justify-between gap-2 text-sm"
              >
                <span className="min-w-0 flex-1 truncate text-gray-600">
                  {item.name}
                </span>
                <span className="shrink-0 font-semibold text-gray-900">
                  {item.price !== null && !item.isAnalysisRequired
                    ? formatPrice(item.price)
                    : 'Analiz'}
                </span>
                {onRemoveType && (
                  <button
                    type="button"
                    onClick={() => onRemoveType(item.id)}
                    aria-label={`${item.name} arızasını kaldır`}
                    className="flex size-6 shrink-0 cursor-pointer items-center justify-center rounded-full bg-gray-100 text-gray-500 transition-colors hover:bg-gray-200"
                  >
                    <X className="size-3" />
                  </button>
                )}
              </li>
            ))}
          </ul>

          <div className="bg-primary/5 rounded-xl px-4 py-3">
            {isWarrantyCovered ? (
              <>
                <p className="text-xs text-gray-600">Garanti kapsamı</p>
                <p className="text-lg font-semibold text-emerald-600">
                  Ücretsiz
                </p>
              </>
            ) : (
              <>
                <p className="text-xs text-gray-600">
                  Ön Teklif Toplamı{' '}
                  <span className="text-gray-400">(KDV dahil)</span>
                </p>
                <p className="text-primary text-2xl font-semibold">
                  {total !== null ? formatPrice(total) : 'İnceleme sonrası'}
                </p>
                {total !== null && (
                  <p className="mt-1 text-[11px] leading-relaxed text-gray-500">
                    Kesin tutar inceleme sonrası netleşir.
                  </p>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {preview && preview.length > 0 && (
        <ul className="space-y-3 border-t border-gray-100 pt-4">
          {preview.map((row) => (
            <li key={row.label}>
              <p className="text-xs text-gray-400">{row.label}</p>
              <p className="text-sm font-medium text-gray-800">{row.value}</p>
            </li>
          ))}
        </ul>
      )}

      <div className="bg-primary/5 flex gap-3 rounded-xl p-4">
        <Info className="text-primary mt-0.5 size-4 shrink-0" />
        <p className="text-xs leading-relaxed text-gray-600">
          Gösterilen tutarlar{' '}
          <strong className="font-semibold">ön tekliftir</strong>; cihazınız
          uzman ekibimiz tarafından kontrol edildikten sonra kesinleşir.
          Onayınız olmadan işlem yapılmaz ve tahsilat gerçekleşmez.
        </p>
      </div>

      <ul className="space-y-4 border-t border-gray-100 pt-4">
        {guarantees.map(({ icon: Icon, title, description }) => (
          <li key={title} className="flex items-start gap-3">
            <span className="bg-primary/10 flex size-9 shrink-0 items-center justify-center rounded-lg">
              <Icon className="text-primary size-4" />
            </span>
            <span className="text-xs leading-relaxed">
              <span className="block text-sm font-medium text-gray-900">
                {title}
              </span>
              <span className="text-gray-500">{description}</span>
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default SelectionCard;
